/**
 * Apps rows — ONE shape for every kind of connection (SYN-2865).
 *
 * 🔴 WHY THIS FILE EXISTS.
 *
 * Maria connected Microsoft 365 on 4 Sep 2026 — credential refreshed, token live,
 * connection active — and then said *"i don't see m365 in the list of apps"*. The nav
 * word had been widened to "Apps" precisely so it would stretch to email and files
 * (`Layout.RANDY_NAV_ITEM`, SYN-2378: *"his till is a system, his email and his files are
 * not, and all three are connections"*). The surface behind the word did not widen with
 * it. `/connections` asked one source (the database store) and
 * rendered two database fields, so a mail connection was not filtered out — it was
 * **unrepresentable**.
 *
 * 🔑 THE RULE THIS MODULE ENFORCES: a row is composed from the facts the row HAS, never
 * from the kind of thing it is. There is no connector name anywhere in this file and no
 * branch on one. `details` is a list that a source contributes to and the view joins; a
 * connector nobody has added yet contributes whatever it carries — possibly only its
 * name — and renders as a row rather than vanishing. That is what makes tenant N+1 work
 * without an edit here (principle 15).
 *
 * 🔴 AND ONE THING IS DROPPED, DELIBERATELY AND BY STATE: a connector the org has merely
 * been OFFERED is not an app it has. That is the only exclusion here, it is written as a
 * state rather than an identity, and it is asserted both ways. Everything else this page
 * cannot show — a read that was refused, a read that failed, a read still out — is said on
 * the page itself, from the leg errors the connector read returns, never inferred from a
 * count taken here. A surface that renders one kind and quietly loses the rest is the
 * defect this file was opened to fix; re-introducing it one predicate lower would be the
 * same bug wearing a different hat.
 */

import { AUTH_STATUS_META, type ConnectionCard } from "../../lib/connections";
import type { DatabaseConnection } from "../../lib/database-connections";

/** The kind a row came from. PRESENTATION ONLY — never a branch on behaviour. */
export type AppRowKind = "database" | "connector";

/**
 * One app, whatever kind it is.
 *
 * `details` is deliberately a LIST, not a template. The old page rendered
 * `{database} · {schema}`, which is why a mail connection could not be shown at all.
 * Every source contributes the descriptive facts it happens to have and the view joins
 * what arrives; an empty list renders a row with just its name and status, which is the
 * honest floor for a kind we have never seen.
 */
export interface AppRow {
  /** Stable across kinds. Prefixed so two sources can never collide on an id. */
  id: string;
  kind: AppRowKind;
  label: string;
  details: string[];
  /** Already in buyer words. Raw state enums never reach the view. */
  statusLabel: string;
  /**
   * True when this app is NOT working as it stands (needs a new password, a reconnect, a check
   * that has not finished, or cannot be reached). A broken app of hers is listed WITH its state,
   * never hidden and never shown as if it were healthy.
   */
  needsAttention: boolean;
  /**
   * The parts INSIDE this app — the second level of the tree (SYN-2627).
   *
   * A list, for the same reason `details` is: a source contributes whatever parts it
   * happens to have and the view renders what arrives. A kind with no parts is not a
   * broken row, it is a leaf, and it renders as one. No source is named here.
   */
  children: AppRowChild[];
  /**
   * SYN-3163. "Update password" for a database connection, or null when there is no working
   * credential to replace (never finished connecting, or revoked).
   *
   * `urgent` when the database has REFUSED the login — the page leads with it then.
   */
  updatePassword: AppRowPasswordUpdate | null;
  /**
   * SYN-3316 (Maria, 3 Oct 2026): ONE Remove for any connection the person made, replacing the
   * separate Disconnect. Null when this person may not remove it: only the server's `can_remove`
   * (database) or `capabilities.canRemove` with its `removeFacts` (held) turns it on, so the
   * control is ABSENT rather than present-and-doomed.
   */
  remove: AppRowRemove | null;
}

/**
 * Everything the one Remove control needs, by kind.
 *
 * Both kinds carry what tells two near-identical names apart on the confirm (SYN-3316 R7):
 * `detail` (where it points). A held connection also carries `lastUsed`, its APP's last use
 * (SYN-1809), with `lastUsedScope` saying what that last use is OF. A database connection
 * carries no last use: Command recorded it in a table Maria dropped on 4 Oct 2026, and no
 * Coherence or Atlas fact holds it (it died with that table; the confirm says nothing about it).
 */
export type AppRowRemove =
  | {
      kind: "database";
      connectionId: string;
      label: string;
      detail: string;
    }
  | {
      kind: "held";
      /** The server's own selector (`conn:<id>`), verified against the held list. Never built here. */
      ref: string;
      label: string;
      detail: string | null;
      lastUsed: string | null;
      lastUsedScope: "app" | "connection";
      /** Routines the SERVER said break, named on the confirm before she acts. */
      breaks: readonly { id: string; name: string }[];
      /** Present for a personal Xero connection, whose removal preview the confirm loads. */
      previewConnectionId?: string;
    };

export interface AppRowPasswordUpdate {
  connectionId: string;
  label: string;
  urgent: boolean;
}

/** One part inside an app. Presentation only; never a branch on behaviour. */
export interface AppRowChild {
  id: string;
  label: string;
  /** A short fact about the part, when it has one. Never a template. */
  detail: string | null;
}

function compact(values: Array<string | null | undefined>): string[] {
  return values.filter((value): value is string => typeof value === "string" && value.length > 0);
}

/**
 * A date a person can read, or nothing.
 *
 * An unparseable timestamp yields NOTHING rather than "Invalid Date". A detail we cannot
 * render is a detail we omit; it is never worth showing a person the word "Invalid".
 */
function dateFact(prefix: string, value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return `${prefix} ${parsed.toLocaleDateString()}`;
}

/**
 * State, in his language.
 *
 * The states are derived from Coherence's facts (database-connections.ts): `not_ready` means
 * it has no runner — the database refused the last password, or it never finished — so the
 * label says what to DO, because he is the only one who can.
 */
export function databaseStateLabel(state: DatabaseConnection["state"]): string {
  if (state === "live") return "Ready";
  if (state === "not_ready") return "Needs a new password";
  if (state === "unhealthy") return "Can't reach it";
  return "Needs checking";
}

export function databaseAppRow(connection: DatabaseConnection): AppRow {
  return {
    id: `database:${connection.id}`,
    kind: "database",
    label: connection.label,
    details: compact([
      connection.database,
      connection.schema,
      dateFact("last checked", connection.last_verified_at),
    ]),
    statusLabel: databaseStateLabel(connection.state),
    needsAttention: connection.state !== "live",
    // No link to a review page: the review step is gone (4 Oct 2026; the structure is read
    // live). And no "ask" link either — Maria, 8 Sep: "remove the ask a question button on the
    // apps page". Asking happens in chat.
    // A database's parts are its tables, read live when a question needs them and never kept
    // (4 Oct 2026). A leaf here is honest; inventing a child from `schema` would be the
    // `{database} · {schema}` mistake again, one level down.
    children: [],
    // SYN-3163. Offered on every state: a listed connection always has a credential in use or
    // needs one, and an expired password nobody has asked through yet still reads `live`.
    updatePassword: {
      connectionId: connection.id,
      label: connection.label,
      urgent: connection.state === "not_ready",
    },
    remove: connection.can_remove
      ? {
          kind: "database",
          connectionId: connection.id,
          label: connection.label,
          detail: compact([
            connection.host
              ? connection.port
                ? `${connection.host}:${connection.port}`
                : connection.host
              : null,
            connection.database,
          ]).join(" · "),
        }
      : null,
  };
}

/**
 * 🔴 THE ONLY FREE-TEXT SERVER STRING THIS PAGE RENDERS IS THE ROW'S NAME.
 *
 * `scopeSummary` and `statusDetail` are deliberately NOT rendered here, and neither is
 * anything else the upstream composes as prose. `scopeSummary` falls back to a connector
 * publisher's own `description` (`scopeSummaryFor`, handlers/connections.ts), so it can
 * carry a `cap.*` id, a snake_case enum or the word "schema" — all of which
 * `test/vocabulary-leakage.ts` counts as buyer-visible leaks. It is also
 * admin-console framing ("lets your routines take 3 kinds of action in…"), which is not
 * what this surface is for. Everything else on a connector row is composed by us: the
 * status comes through AUTH_STATUS_META rather than `statusDetail`, and the date is
 * formatted here from a timestamp.
 *
 * The NAME is the one exception, and it is a named one. A connector whose display name
 * looks internal is a registry data-quality problem, visible identically on
 * /workspace/connections. Suppressing the name would hide the app — which is the defect
 * this file exists to fix. Better a badly-named app than an absent one.
 */
export function connectorAppRow(card: ConnectionCard): AppRow {
  return {
    // connectionId identifies the API type; ref identifies this held instance
    // (or catalog card). Both Apps surfaces need a distinct key for each.
    id: `connector:${card.ref}`,
    kind: "connector",
    label: card.displayName,
    details: compact([dateFact("last used", card.lastUsed)]),
    // SYN-3335: a TOKEN compared, never rendered. A Xero business whose connection check
    // has not passed gets words that say so, instead of a bare "Action needed".
    statusLabel:
      card.statusDetail === "verification_pending"
        ? "Connection check not finished"
        : AUTH_STATUS_META[card.authStatus].label,
    needsAttention: card.authStatus !== "connected" || card.statusDetail === "verification_pending",
    children: card.subBundles.map(subBundleChild),
    remove: heldRemoveTarget(card),
    // A connector's credential is a provider sign-in, renewed at the provider — not here.
    updatePassword: null,
  };
}

/**
 * One sub-bundle, as a child node.
 *
 * The label is the sub-bundle's display name — the same free-text-from-the-registry
 * exception the row's own name already is, and named for the same reason: suppressing it
 * would hide the part. The detail is COMPOSED HERE from a count, never taken as prose, so
 * a publisher's `description` (which can carry a `cap.*` id or a snake_case enum — see the
 * note on `connectorAppRow` below) never reaches this surface.
 */
function subBundleChild(subBundle: ConnectionCard["subBundles"][number]): AppRowChild {
  return {
    id: `part:${subBundle.ref}`,
    label: subBundle.displayName,
    detail:
      subBundle.toolCount > 0
        ? `${subBundle.toolCount} ${subBundle.toolCount === 1 ? "thing it can do" : "things it can do"}`
        : null,
  };
}

/**
 * Whether, and how, this held app can be Removed (SYN-3316). The ONE gate, used by the Apps page
 * and by the admin page's detail (Maria: the admin Disconnect became the same Remove), so the two
 * can never disagree about who may remove what.
 *
 * 🔴 FAIL CLOSED, FROM THE SERVER'S OWN ANSWER. `canRemove` is decided server-side (the caller
 * made this connection) and `removeFacts` carries what tells it apart. Either absent: no
 * control. `canRevoke` no longer decides anything here: Maria replaced the separate Disconnect
 * with ONE Remove for the person who made the connection.
 *
 * The `breaks` list is carried so the confirm can NAME what stops working before she acts.
 */
export function heldRemoveTarget(card: ConnectionCard): AppRowRemove | null {
  const facts = card.removeFacts;
  if (!card.capabilities.canRemove || !facts) {
    return null;
  }
  return {
    kind: "held",
    ref: card.ref,
    label: facts.label,
    detail: facts.detail,
    lastUsed: facts.lastUsed,
    lastUsedScope: facts.lastUsedScope,
    breaks: card.breaks,
    ...(card.connection?.bindingVersion !== undefined
      ? { previewConnectionId: card.connection.id }
      : {}),
  };
}

/**
 * Which connector cards are apps the org HOLDS.
 *
 * The composed connector read serves two jobs at once: cards for connections that exist,
 * and cards for connectors merely seeded into the org and never authenticated. Only the
 * first belongs under "Apps" — otherwise a new org's ~20 seed connectors would all read
 * as things it has.
 *
 * 🔑 The predicate is a STATE, not an identity. `available_to_connect` is exactly the
 * never-authenticated seed (see `resolveAuthStatus` in lib/connections.ts, SYN-1900);
 * everything else — connected, expired, revoked, action needed — is a connection the org
 * has, whoever supplies it. No connector is named here and none ever should be.
 */
export function isHeldConnectorCard(card: ConnectionCard): boolean {
  // 🔑 THE NAMED STOP BELOW IS NOW CLOSED, and closed the way it said it should be.
  //
  // A card that names a held connection IS one the org has, whatever we could learn about
  // its health. `authStatus` is derived from live readiness, so when that leg came back
  // empty EVERY card was demoted to `available_to_connect` and this predicate dropped it —
  // an app the org genuinely holds vanished from a list that still looked finished. That
  // is the exact defect SYN-2865 was opened for, one predicate lower.
  //
  // The signal is `connection`, published by the server since SYN-2844 (PR #247, on `main`)
  // and never read on the client until now. `authStatus` remains the fallback for a card
  // with no connection identity — an api-grain bundle card — where the state is still the
  // only thing we have.
  // 🔴 `?.id` and not `!== null`: a card parsed by an older build, or any object that
  // never carried the field, arrives `undefined` — and `undefined !== null` is TRUE, which
  // would have flipped this predicate to "everything is held" and put every seed connector
  // in an org's Apps list. Fail closed means checking for the id, not for the absence of
  // one particular empty value.
  if (card.connection?.id) {
    return true;
  }
  return card.authStatus !== "available_to_connect";
}

/**
 * ✅ CLOSED BY SYN-2627 — kept because the reasoning is the reason the fix is shaped as it
 * is, and because the FALLBACK arm still carries the hole for a card with no connection
 * identity. The original note follows.
 *
 * 🛑 NAMED STOP — the server conflates "we could not read live state" with "there is no
 * credential", and this predicate inherited it.
 *
 * `composeConnections` sets `registered = group.hasReadiness`
 * (src/api/handlers/connections.ts:1085), so when the live connector-state leg yields
 * nothing, EVERY card comes back `registered: false` / `action_needed`; the parser calms
 * that to `available_to_connect` (lib/connections.ts, `resolveAuthStatus`); this predicate
 * then drops it. A connection the org genuinely holds disappears from Apps.
 *
 * The page covers the case that ANNOUNCES itself: a leg that failed sets
 * `errors.connectorState`, and the page says the answer is partial. The case that does not
 * announce itself is the branch at src/api/handlers/connections.ts:1642 — a provider
 * without `getConnectorReadiness` resolves as a SUCCESS carrying zero connectors, so the
 * read reports itself complete while every card is demoted.
 *
 * How reachable, measured rather than assumed: `getProvider` falls back to
 * `getCoherenceClient()`, whose `CoherenceClient` declares `getConnectorReadiness` as a
 * class method (src/coherence/client.ts:633), and `MeteredCoherenceProvider` copies it
 * when the delegate has it. So the silent branch is not on the production path today; it
 * is reachable only through an injected `req.provider`. That makes it a latent hole, not a
 * live one — and "not live today" is exactly how a dormant mechanism reads healthy until
 * something needs it.
 *
 * The fix is a server one: a card must carry whether the org HOLDS a credential separately
 * from whether we could read its live health, so the client can show a held connection
 * whose state is unknown instead of hiding it. cloch-ai/command PR #247 (SYN-2844) already
 * adds a per-connection `connection: {id, subjectType, subjectId}` field, which is that
 * signal; once it lands this predicate should prefer it. Both live in
 * `src/api/handlers/connections.ts`, which this change may not edit and which that PR has
 * open, so this is filed, not fixed. Raised by the pass-2 directed review as G2.
 */

/**
 * Every app the person has, in one list.
 *
 * Sorted by NAME, not by kind. Ordering by kind would re-encode the assumption this
 * ticket exists to remove — that a database is the real thing and everything else is an
 * extra. `id` breaks ties so the order is stable across renders.
 */
export function toAppRows(input: {
  databases?: readonly DatabaseConnection[] | null;
  connectors?: readonly ConnectionCard[] | null;
}): AppRow[] {
  const ownDatabases = (input.databases ?? []).filter(isOwnNamedDatabase);
  const databaseIds = new Set((input.databases ?? []).map((database) => `conn:${database.id}`));
  const seen = new Set<string>();
  const rows: AppRow[] = [
    ...ownDatabases.map(databaseAppRow),
    ...(input.connectors ?? [])
      .filter(isOwnConnectionInstance)
      // ONE row per connection. A database connection lives in Coherence, so Coherence's held
      // list carries it too (`conn:<id>`, SYN-3316 R1); its database row is the one that can
      // update its password and ask it something, so that row stands for it. (A database's held
      // card is org-subject, so it is not the caller's own above either; this stays as the
      // belt to that brace.)
      .filter((card) => !databaseIds.has(card.ref))
      // ONE row per connection_id, whatever the upstream sends twice.
      .filter((card) => {
        const id = card.connection?.id ?? card.ref;
        if (seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      })
      .map(connectorAppRow),
  ];
  return rows.toSorted((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id));
}

/**
 * 🔴 THE APPS RULE (Maria, 4 Oct 2026): "The Apps tree is ONE read of the connections the user
 * SET UP, by the names they gave them. what is in the apps page is the list of connection
 * instances the user has set up and the user can choose to remove them or update the
 * credentials/settings. that's it."
 *
 * So a connector card is an app only when it IS a connection instance (it names a
 * `connection.id`) AND the server says that instance is the caller's own (`ownedByCaller`, D-28:
 * its credential subject is this person). Everything else the composed read carries is not an
 * app of hers and is not listed:
 *
 * - a CONNECTOR TYPE card (`connection: null`). Measured on cloch, 6 Oct 2026: "Microsoft Graph"
 *   beside "Microsoft 365 · maria@…". That label is `refLabel("microsoft-graph")`, the shared
 *   tool-name prefix of the microsoft-graph bundles (`bundleApiGrain`,
 *   src/api/handlers/connections.ts). It is the TYPE her Microsoft 365 connection is on, kept
 *   as its own card because the api-level readiness was not "connected" (SYN-3334's fold keeps
 *   it deliberately, as a signal for the admin page). `isHeldConnectorCard` let it through on
 *   that status alone. It has no connection id, so it is no instance of anything.
 * - an ORG-SUBJECT connection (the shared model from before D-28): it is the org's, not hers.
 * - an evidence-only card and a seed: no connection id.
 *
 * The admin page (/workspace/connections) still shows all of them; it is the org's view.
 */
export function isOwnConnectionInstance(card: ConnectionCard): boolean {
  return Boolean(card.connection?.id) && card.connection?.ownedByCaller === true;
}

/**
 * A database is listed when it is one the person SET UP and can be told apart by something they
 * gave it: its name, or failing that the database it points at.
 *
 * Coherence lists only databases whose recorded creator is the caller (D-28: a database is
 * org-subject by construction, so its owner is `created_by`). Every database made through the
 * current connect path carries both a name (Command's connect refuses one without a label) and
 * its target (written by the provisioner). One that carries NEITHER predates that path: its name
 * and address lived in Command's randy tables, dropped on 4 Oct 2026 (#577), so all that is left
 * to call it is its ENGINE, which is a connector type, never a name she gave. Measured on cloch,
 * 6 Oct 2026: such a connection rendered as "postgres database" in her tree. It is not listed as
 * hers. Nothing about it is removed, revoked or changed: it is only not presented as an app she
 * set up.
 */
export function isOwnNamedDatabase(connection: DatabaseConnection): boolean {
  return connection.named === true || Boolean(connection.database);
}
